import { api } from '../api/client';
import { API_ADMIN_DISCOVER_URL, API_DISCOVER_CATEGORIES_URL, API_GLOBAL_COLLECTIONS_URL } from '../utils';

// Discover is curated (keepr-api docs/SCHEMA.md § Discover is curated, KPR-387):
// the category list a listing is chosen from, the Featured row, and the
// packages that point into a global collection. Errors propagate: every
// caller shows its own words for a refusal.

/** The names a listing's category is chosen from, in display order. */
export async function getCategories() {
	const { data } = await api.get(API_DISCOVER_CATEGORIES_URL);
	return Array.isArray(data?.categories) ? data.categories : [];
}

/** `{ categories, offList, featured }` (admin). */
export async function getAdminDiscover() {
	const { data } = await api.get(API_ADMIN_DISCOVER_URL);
	return {
		categories: Array.isArray(data?.categories) ? data.categories : [],
		offList: Array.isArray(data?.offList) ? data.offList : [],
		featured: Array.isArray(data?.featured) ? data.featured : [],
	};
}

/** Replaces the list. → `{ categories, offList }`. */
export async function saveCategories(categories) {
	const { data } = await api.put(`${API_ADMIN_DISCOVER_URL}/categories`, { categories });
	return data;
}

/** The whole Featured row, in order: `[{ type: 'package' | 'collection', id }]`. → `{ featured }`. */
export async function saveFeatured(items) {
	const { data } = await api.put(`${API_ADMIN_DISCOVER_URL}/featured`, { items });
	return data;
}

/** The words Discover search treats as one: `[[word, word…]…]`, `[]` when none (admin). */
export async function getSynonyms() {
	const { data } = await api.get(`${API_ADMIN_DISCOVER_URL}/synonyms`);
	return Array.isArray(data?.groups) ? data.groups : [];
}

/** Replaces the whole list. → `[[word…]…]` as stored. */
export async function saveSynonyms(groups) {
	const { data } = await api.put(`${API_ADMIN_DISCOVER_URL}/synonyms`, { groups });
	return Array.isArray(data?.groups) ? data.groups : groups;
}

/** What people searched for and found nothing, most counted first: `[{ words, count, lastSeen }]` (admin). */
export async function getUnfound(days = 30) {
	const { data } = await api.get(`${API_ADMIN_DISCOVER_URL}/unfound`, { params: { days } });
	return Array.isArray(data) ? data : [];
}

/** The published packages that point into a global collection: `[{ key, name, kind, icon, color }]`. */
export async function getUsedBy(collectionId) {
	const { data } = await api.get(`${API_GLOBAL_COLLECTIONS_URL}/${collectionId}/used-by`);
	return Array.isArray(data) ? data : [];
}

/** Compares category names the way the server does: ignoring case and spacing at the ends. */
export const sameCategory = (a, b) => String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();

/** The list's own spelling of `name`, or null when it is not on the list. */
export function listedCategory(categories, name) {
	return (categories || []).find(c => sameCategory(c, name)) || null;
}

/** The words for a refusal of the curation routes. */
export function discoverRefusal(error, fallback = 'That did not work. Trying again may help.') {
	const body = error?.response?.data || {};
	switch (body.code) {
		case 'category_not_listed': return body.message || 'Choose one of Discover\'s categories.';
		case 'invalid_categories': return body.message || 'Those categories could not be saved.';
		case 'not_published': return 'Only published packages and global collections can be featured.';
		case 'invalid_synonyms': return body.message || 'Those synonyms could not be saved. Each group needs 2 to 10 single words, and no word can be in two groups.';
		case 'invalid_featured': return body.message || 'That Featured row could not be saved.';
		default: return body.message || fallback;
	}
}
